import { useState, useEffect } from 'react'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Equipment } from '@/types'

interface EditEquipmentModalProps {
  isOpen: boolean
  equipment: Equipment
  onClose: () => void
  onSubmit: (equipmentData: Partial<Equipment>) => Promise<void>
}

export default function EditEquipmentModal({ isOpen, equipment, onClose, onSubmit }: EditEquipmentModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'hvac' as Equipment['type'],
    manufacturer: '',
    model: '',
    serial_number: '',
    install_date: '',
    warranty_expiry: '',
    location: {
      building: '',
      floor: '',
      room: ''
    },
    specifications: {
      capacity: '',
      voltage: '',
      power: ''
    },
    status: 'operational' as 'operational' | 'maintenance' | 'offline',
    health_score: 100
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Initialize form with equipment data
  useEffect(() => {
    if (equipment) {
      setFormData({
        name: equipment.name || '',
        type: equipment.type || 'hvac',
        manufacturer: equipment.manufacturer || '',
        model: equipment.model || '',
        serial_number: equipment.serialNumber || equipment.serial_number || '',
        install_date: equipment.installDate || equipment.install_date || '',
        warranty_expiry: equipment.warrantyExpiry || equipment.warranty_expiry || '',
        location: {
          building: equipment.location?.building || '',
          floor: equipment.location?.floor || '',
          room: equipment.location?.room || ''
        },
        specifications: {
          capacity: equipment.specifications?.capacity || '',
          voltage: equipment.specifications?.voltage || '',
          power: equipment.specifications?.power || ''
        },
        status: equipment.status === 'excellent' || equipment.status === 'good' || equipment.status === 'warning' || equipment.status === 'poor' || equipment.status === 'critical' ? 'operational' : equipment.status === 'operational' || equipment.status === 'maintenance' || equipment.status === 'offline' ? equipment.status : 'operational',
        health_score: equipment.healthScore || equipment.health_score || 100
      })
    }
  }, [equipment])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    
    if (name.startsWith('location.')) {
      const field = name.split('.')[1]
      setFormData(prev => ({ 
        ...prev, 
        location: { ...prev.location, [field]: value }
      }))
    } else if (name.startsWith('specifications.')) {
      const field = name.split('.')[1]
      setFormData(prev => ({ 
        ...prev, 
        specifications: { ...prev.specifications, [field]: value }
      }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Equipment name is required'
    }
    if (!formData.type) {
      newErrors.type = 'Equipment type is required'
    }
    if (formData.health_score < 0 || formData.health_score > 100) {
      newErrors.health_score = 'Health score must be between 0 and 100'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setIsSubmitting(true)
    try {
      const submitData: Partial<Equipment> = {
        name: formData.name.trim(),
        type: formData.type,
        manufacturer: formData.manufacturer.trim() || undefined,
        model: formData.model.trim() || undefined,
        serial_number: formData.serial_number.trim() || undefined,
        install_date: formData.install_date || undefined,
        warranty_expiry: formData.warranty_expiry || undefined,
        location: {
          propertyId: equipment.location?.propertyId,
          building: formData.location.building.trim() || undefined,
          floor: formData.location.floor.trim() || undefined,
          room: formData.location.room.trim() || undefined
        },
        specifications: {
          capacity: formData.specifications.capacity.trim() || undefined,
          voltage: formData.specifications.voltage.trim() || undefined,
          power: formData.specifications.power.trim() || undefined
        },
        status: formData.status,
        health_score: formData.health_score
      }
      
      await onSubmit(submitData)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug('Failed to update equipment:', error)
      }
      // Error handling can be added here (e.g., toast notification)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Edit Equipment</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Equipment Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="HVAC Unit 1"
                    className={errors.name ? 'border-red-300' : ''}
                  />
                  {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="type">Equipment Type *</Label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="hvac">HVAC</option>
                    <option value="plumbing">Plumbing</option>
                    <option value="electrical">Electrical</option>
                    <option value="elevators">Elevators</option>
                    <option value="security">Security</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.type && <p className="text-sm text-red-600 mt-1">{errors.type}</p>}
                </div>

                <div>
                  <Label htmlFor="manufacturer">Manufacturer</Label>
                  <Input
                    id="manufacturer"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleChange}
                    placeholder="Carrier"
                  />
                </div>

                <div>
                  <Label htmlFor="model">Model</Label>
                  <Input
                    id="model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="25HCB4"
                  />
                </div>

                <div>
                  <Label htmlFor="serial_number">Serial Number</Label>
                  <Input
                    id="serial_number"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleChange}
                    placeholder="ABC123456"
                  />
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="operational">Operational</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="offline">Offline</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="location.building">Building</Label>
                  <Input
                    id="location.building"
                    name="location.building"
                    value={formData.location.building}
                    onChange={handleChange}
                    placeholder="Main Building"
                  />
                </div>

                <div>
                  <Label htmlFor="location.floor">Floor</Label>
                  <Input
                    id="location.floor"
                    name="location.floor"
                    value={formData.location.floor}
                    onChange={handleChange}
                    placeholder="2"
                  />
                </div>

                <div>
                  <Label htmlFor="location.room">Room</Label>
                  <Input
                    id="location.room"
                    name="location.room"
                    value={formData.location.room}
                    onChange={handleChange}
                    placeholder="201"
                  />
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Specifications</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="specifications.capacity">Capacity</Label>
                  <Input
                    id="specifications.capacity"
                    name="specifications.capacity"
                    value={formData.specifications.capacity}
                    onChange={handleChange}
                    placeholder="5 tons"
                  />
                </div>

                <div>
                  <Label htmlFor="specifications.voltage">Voltage</Label>
                  <Input
                    id="specifications.voltage"
                    name="specifications.voltage"
                    value={formData.specifications.voltage}
                    onChange={handleChange}
                    placeholder="240V"
                  />
                </div>

                <div>
                  <Label htmlFor="specifications.power">Power</Label>
                  <Input
                    id="specifications.power"
                    name="specifications.power"
                    value={formData.specifications.power}
                    onChange={handleChange}
                    placeholder="15kW"
                  />
                </div>
              </div>
            </div>

            {/* Dates and Health */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="install_date">Install Date</Label>
                  <Input
                    id="install_date"
                    name="install_date"
                    type="date"
                    value={formData.install_date}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label htmlFor="warranty_expiry">Warranty Expiry</Label>
                  <Input
                    id="warranty_expiry"
                    name="warranty_expiry"
                    type="date"
                    value={formData.warranty_expiry}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label htmlFor="health_score">Health Score (0-100)</Label>
                  <Input
                    id="health_score"
                    name="health_score"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.health_score}
                    onChange={handleChange}
                    className={errors.health_score ? 'border-red-300' : ''}
                  />
                  {errors.health_score && <p className="text-sm text-red-600 mt-1">{errors.health_score}</p>}
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? 'Updating...' : 'Update Equipment'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}